import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  TablePagination,
} from "@mui/material";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaEdit, FaTrash } from "react-icons/fa";
import authService from "../../../utils/authService";
import { getAdminCompanyListCached } from "../../../utils/adminCompanyCache";

interface Contact {
  id?: number;
  name: string;
  designation: string;
  email: string;
  phone_no: string;
  password: string;
  company_id: number | string;
  del_row?: boolean;
}

interface Company {
  company_id: string;
  company_name: string;
  del_row?: boolean;
}

const Contacts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<Contact>({
    name: "",
    designation: "",
    email: "",
    phone_no: "",
    password: "",
    company_id: "",
  });
  const [error, setError] = useState<string>("");
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedContact, setSelectedContact] = useState<Contact>();
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalContacts, setTotalContacts] = useState(0);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const companyFromUrl = searchParams.get("company");
    if (companyFromUrl && companies.length > 0) {
      const exists = companies.some((c) => String(c.company_id) === companyFromUrl);
      if (exists) setSelectedCompany(companyFromUrl);
    }
  }, [searchParams, companies]);

  useEffect(() => {
    if (selectedCompany) {
      fetchContacts(selectedCompany);
    } else {
      setContacts([]);
      setTotalContacts(0);
    }
  }, [selectedCompany, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [selectedCompany, search, sortField, sortDirection]);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const { companies } = await getAdminCompanyListCached();
      setCompanies(((companies || []) as Company[]).filter((c) => !c.del_row));
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchContacts = async (companyId: string) => {
    try {
      setLoading(true);
      setContacts([]);
      const apiPage = page + 1;
      const response = await authService.get(
        `placement-officer/company-contacts/company/${companyId}?page=${apiPage}&count=${rowsPerPage}`
      );
      const payload = response.data ?? {};
      const rows = Array.isArray(payload.contacts) ? payload.contacts : [];
      const total = typeof payload.total === "number" ? payload.total : rows.length;
      setContacts(rows);
      setTotalContacts(total);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching contacts:", error);
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value) setLoading(true);
    setSelectedCompany(value);
    setSearch("");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("company", value);
        else next.delete("company");
        return next;
      },
      { replace: true }
    );
  };

  const handleModal = (type: string, contact?: Contact): void => {
    setError("");
    setModalType(type);
    if (type === "new") {
      setFormData({ name: "", designation: "", email: "", phone_no: "", password: "", company_id: selectedCompany });
    } else if (contact) {
      setFormData(contact);
    }
    setShowModal(true);
  };

  const generatePassword = (): string => {
    const alpha = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const special = "!@#$%^&*";
    const all = alpha + numbers + special;
    let pwd = "";
    pwd += alpha[Math.floor(Math.random() * alpha.length)];
    pwd += numbers[Math.floor(Math.random() * numbers.length)];
    pwd += special[Math.floor(Math.random() * special.length)];
    for (let i = 3; i < 8; i++) pwd += all[Math.floor(Math.random() * all.length)];
    return pwd.split("").sort(() => Math.random() - 0.5).join("");
  };

  const handleGeneratePassword = (): void => {
    setFormData((prev) => ({ ...prev, password: generatePassword() }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    if (name === "phone_no") {
      const numeric = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, phone_no: numeric }));
      return;
    }
    if (name === "name" || name === "designation") {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/[^a-zA-Z\s]/g, "") }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (): Promise<void> => {
    setError("");

    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      setError("Name must contain only letters and spaces");
      return;
    }
    if (!formData.designation.trim()) {
      setError("Designation is required");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(formData.designation.trim())) {
      setError("Designation must contain only letters and spaces");
      return;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    if (!formData.phone_no.trim()) {
      setError("Phone number is required");
      return;
    }
    if (!/^\d{10}$/.test(formData.phone_no.trim())) {
      setError("Phone number must be exactly 10 digits");
      return;
    }
    if (!formData.password.trim()) {
      setError("Password is required");
      return;
    }

    try {
      setLoading(true);
      if (modalType === "new") {
        await authService.post("placement-officer/company-contacts/create", {
          name: formData.name,
          designation: formData.designation,
          email: formData.email,
          phone_no: formData.phone_no,
          password: formData.password,
          company_id: selectedCompany,
          created_by: sessionStorage.getItem("Email") || "",
        });
      } else {
        await authService.put("placement-officer/company-contacts/update", {
          id: formData.id,
          name: formData.name,
          designation: formData.designation,
          email: formData.email,
          phone_no: formData.phone_no,
          password: formData.password,
          updated_by: sessionStorage.getItem("Email") || "",
        });
      }
      setLoading(false);
      setShowModal(false);
      fetchContacts(selectedCompany);
    } catch (err: any) {
      setLoading(false);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    }
  };

  const handleDelete = (contact: Contact): void => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (selectedContact) {
      try {
        setDeleting(true);
        await authService.delete("placement-officer/company-contacts/delete", {
          id: selectedContact.id,
        });
        setDeleting(false);
        fetchContacts(selectedCompany);
        setShowDeleteModal(false);
      } catch (error) {
        setDeleting(false);
        console.error("Error deleting contact:", error);
      }
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortedContacts = (): Contact[] => {
    const filtered = contacts
      .filter((c) => !c.del_row)
      .filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.email.toLowerCase().includes(search.toLowerCase()) ||
          c.designation.toLowerCase().includes(search.toLowerCase())
      );

    if (!sortField) return filtered;

    return [...filtered].sort((a, b) => {
      const aValue = (a[sortField as keyof Contact] as string)?.toLowerCase() ?? "";
      const bValue = (b[sortField as keyof Contact] as string)?.toLowerCase() ?? "";
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  const sortedContacts = getSortedContacts();

  const SortIcon = ({ field }: { field: string }) =>
    sortField === field ? (
      sortDirection === "asc" ? (
        <PiSortDescendingLight size={16} />
      ) : (
        <PiSortAscendingLight size={16} />
      )
    ) : (
      <HiOutlineDotsVertical size={16} />
    );

  const columns: { label: string; field: string }[] = [
    { label: "Name", field: "name" },
    { label: "Designation", field: "designation" },
    { label: "Email", field: "email" },
    { label: "Phone No", field: "phone_no" },
    { label: "Password", field: "password" },
  ];

  return (
    <Box
      className="border rounded-2 bg-white my-2 me-2"
      sx={{ height: "calc(100vh - 20px)", overflow: "hidden", display: "flex", flexDirection: "column" }}
    >
      {/* Top bar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        flexShrink={0}
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="contacts-company-label">Company</InputLabel>
            <Select
              labelId="contacts-company-label"
              value={selectedCompany}
              onChange={handleCompanyChange}
              label="Company"
              sx={{ height: 36 }}
            >
              <MenuItem value="">Select Company</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.company_id} value={c.company_id}>
                  {c.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!selectedCompany}
            sx={{ minWidth: 200 }}
          />
        </Box>

        {selectedCompany && (
          <button
            className="btn"
            onClick={() => handleModal("new")}
          >
            + New Contact
          </button>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} className="px-2" sx={{ flexGrow: 1, overflow: "auto", borderRadius: 0 }}>
        <Table className="border border-2" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className="muifont mui-header-cell" sx={{ color: "text.secondary" }}>
                S.No
              </TableCell>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  className="muifont mui-header-cell mui-sortable-header"
                  onClick={() => handleSort(col.field)}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {col.label}
                    </Box>
                    <Box component="span" sx={{ ml: 0.5 }}>
                      <SortIcon field={col.field} />
                    </Box>
                  </Box>
                </TableCell>
              ))}
              <TableCell className="muifont mui-header-cell" sx={{ borderRight: "none" }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(searchParams.get("company") && (loadingCompanies || !selectedCompany || loading)) ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2.5, px: 2.5 }}>
                  <Spinner animation="border" size="sm" />
                </TableCell>
              </TableRow>
            ) : !selectedCompany ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2.5, px: 2.5, color: "text.secondary" }}>
                  Please select a company to view contacts
                </TableCell>
              </TableRow>
            ) : loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2.5, px: 2.5 }}>
                  <Spinner animation="border" size="sm" />
                </TableCell>
              </TableRow>
            ) : sortedContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 2.5, px: 2.5, color: "text.secondary" }}>
                  No contacts found
                </TableCell>
              </TableRow>
            ) : (
              sortedContacts.map((contact, index) => (
                <TableRow
                  key={contact.id ?? index}
                  sx={{
                    bgcolor: index % 2 === 0 ? "background.paper" : "grey.50",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  <TableCell className="muifont mui-body-cell">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell className="muifont mui-body-cell">{contact.name}</TableCell>
                  <TableCell className="muifont mui-body-cell">{contact.designation}</TableCell>
                  <TableCell className="muifont mui-body-cell">{contact.email}</TableCell>
                  <TableCell className="muifont mui-body-cell">{contact.phone_no}</TableCell>
                  <TableCell className="muifont mui-body-cell">{"•".repeat(Math.min((contact.password?.length ?? 0), 10)) || "-"}</TableCell>
                  <TableCell className="muifont mui-body-cell" sx={{ borderRight: "none" }}>
                    <Box
                      component="span"
                      className="fs-6 me-2"
                      role="button"
                      title="Edit"
                      onClick={() => handleModal("edit", contact)}
                      sx={{ cursor: "pointer", display: "inline-flex" }}
                    >
                      <FaEdit />
                    </Box>
                    <Box
                      component="span"
                      className="fs-6 me-2"
                      title="Delete"
                      role="button"
                      onClick={() => handleDelete(contact)}
                      sx={{ cursor: "pointer", display: "inline-flex" }}
                    >
                      <FaTrash />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedCompany && (sortedContacts.length > 0 || totalContacts > 0) && (
        <TablePagination
          component="div"
          count={totalContacts}
          page={page}
          onPageChange={(_, newPage) => {
            setLoading(true);
            setPage(newPage);
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setLoading(true);
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            flexShrink: 0,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#fafafa",
            "& .MuiTablePagination-toolbar": { minHeight: 44, px: 2 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "12px",
              color: "#666",
              mb: 0,
            },
            "& .MuiTablePagination-actions": { ml: 1 },
            "& .MuiIconButton-root": { p: "6px" },
            "& .Mui-disabled": { opacity: 0.45 },
          }}
        />
      )}

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalType === "new" ? "Add New Contact" : "Edit Contact"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {error && <p className="text-danger">{error}</p>}
            {[
              { label: "Name", name: "name", placeholder: "Enter Name" },
              { label: "Designation", name: "designation", placeholder: "Enter Designation" },
              { label: "Email", name: "email", placeholder: "Enter Email" },
            ].map((field) => (
              <Form.Group
                key={field.name}
                controlId={`form-${field.name}`}
                className="mt-3 d-flex align-items-center justify-content-between"
              >
                <Form.Label>{field.label}</Form.Label>
                <Box sx={{ width: "55%" }}>
                  <Form.Control
                    type="text"
                    name={field.name}
                    value={(formData as any)[field.name] ?? ""}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    className="w-100"
                  />
                </Box>
              </Form.Group>
            ))}
            <Form.Group
              controlId="form-phone_no"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label>Phone No</Form.Label>
              <Box sx={{ width: "55%" }}>
                <Form.Control
                  type="tel"
                  name="phone_no"
                  inputMode="numeric"
                  value={formData.phone_no ?? ""}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (!/[\d\b]/.test(e.key) && !["ArrowLeft","ArrowRight","Delete","Tab","Backspace"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter 10-digit Phone Number"
                  maxLength={10}
                  className="w-100"
                />
              </Box>
            </Form.Group>
            <Form.Group
              controlId="form-password"
              className="mt-3"
            >
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="mb-0">Password</Form.Label>
                <Button size="sm" variant="outline-primary" onClick={handleGeneratePassword} type="button">
                  Generate
                </Button>
              </div>
              <Form.Control
                type="text"
                name="password"
                value={formData.password ?? ""}
                onChange={handleChange}
                placeholder="Enter Password or click Generate"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the contact:{" "}
            <strong>{selectedContact?.name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? <><Spinner animation="border" size="sm" className="me-1" />Deleting...</> : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default Contacts;
